import APCMCallDialog from "@/components/APCMCallDialog";
import ScheduleAPCMCallsDialog from "@/components/ScheduleAPCMCallsDialog";
import ScheduleAPCMCheckinCallsDialog from "@/components/ScheduleAPCMCheckinCallsDialog";
import ScheduleBatchCallsDialog from "@/components/ScheduleBatchCallsDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { useConsentsStore } from "@/stores/consentsStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { usePatientsStore } from "@/stores/patientsStore";
import { useVerifiedPhoneNumbersStore } from "@/stores/verifiedPhoneNumbersStore";
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import PatientTable from "@/components/patients/PatientTable";
import PatientPagination from "@/components/patients/PatientPagination";
import PatientActionBar from "@/components/patients/PatientActionBar";
import PatientSelectionSidebar from "@/components/patients/PatientSelectionSidebar";
import PatientFileUploadModal from "@/components/patients/PatientFileUploadModal";
import PatientGoogleDriveImportModal from "@/components/patients/PatientGoogleDriveImportModal";
import PatientFilterBuilder from "@/components/PatientFilterBuilder";
import CompactSentenceFilter, { CompactSentenceFilterRef } from "@/components/CompactSentenceFilter";
import { patientFilterConfig } from "@/config/patientFilterConfig";
import { PatientFilterResult, PatientQueryResults } from "@/config/patientFilterConfig";

/**
 * Quick-search semantics (no LLM involved). One text box, mapped to a single
 * v3 structured filter:
 *  - empty                          -> no filter
 *  - only digits, 10+ chars         -> { patient_phone }       (looks like a phone number; substring match)
 *  - no spaces and contains a digit -> { patient_identifier }  (MRN / external id / FHIR id; case-insensitive substring)
 *  - anything else                  -> { patient_name }        (first/last/preferred name; substring. The backend also
 *                                                                 accepts an exact MRN / FHIR id here as a fallback)
 * The v3 endpoint ANDs its filters, so we deliberately send exactly one key
 * rather than trying to OR phone and identifier in a single request.
 */
const quickSearchToFilters = (raw: string): PatientFilterResult | null => {
  const text = raw.trim();
  if (!text) return null;
  const hasDigit = /\d/.test(text);
  const hasSpace = /\s/.test(text);
  if (/^\d{10,}$/.test(text)) return { patient_phone: text };
  if (hasDigit && !hasSpace) return { patient_identifier: text };
  return { patient_name: text };
};

const shallowEqualFilters = (a: object | null, b: object | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  return ak.length === bk.length && ak.every((k) => (a as any)[k] === (b as any)[k]);
};

const PatientsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const sentenceFilterRef = useRef<CompactSentenceFilterRef>(null);
  const {
    patients,
    isLoading,
    error,
    fetchPatientsAdvanced,
    fetchPatientsV3,
    clearSmartSearch,
    setSmartSearch,
    setStructuredFilters,
    smartSearchQuery,
    smartSearchFilters,
    structuredFilters,
    currentPage,
    setCurrentPage,
    sortingState,
    setSortingState,
    pageSize,
    setPageSize,
    totalPages,
    filters,
    selectedPatients,
    removePatientSelection,
    clearSelections,
  } = usePatientsStore();

  // Search / filter state lives in the persisted patients store so it survives
  // navigating into a patient and back.
  const activeStructuredFilters = structuredFilters as PatientFilterResult | null;
  const sentenceSearchQuery = smartSearchQuery ?? "";
  const sentenceSearchFilters = smartSearchFilters as PatientFilterResult | null;

  const [currentSortBy, setCurrentSortBy] = useState<string>("created_at:desc");

  // Plain quick search (name / phone / MRN / FHIR id). Seeded from the persisted
  // structured filters so the box still shows the term after navigating back.
  const [quickSearch, setQuickSearch] = useState<string>(() => {
    const f = structuredFilters as PatientFilterResult | null;
    if (!f || Object.keys(f).length !== 1) return "";
    return f.patient_identifier ?? f.patient_name ?? f.patient_phone ?? "";
  });
  const quickSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyQuickSearch = (raw: string) => {
    if (quickSearchDebounceRef.current) {
      clearTimeout(quickSearchDebounceRef.current);
      quickSearchDebounceRef.current = null;
    }
    const next = quickSearchToFilters(raw);
    const current = structuredFilters as PatientFilterResult | null;
    if (next === null) {
      // Only clear filters the quick search itself produced; leave builder
      // filters alone.
      if (current && shallowEqualFilters(current, quickSearchToFilters(quickSearch))) {
        clearSmartSearch();
      }
      return;
    }
    if (shallowEqualFilters(current, next)) return;
    // Routes through the store like the filter builder does: clears the
    // sentence search, resets the page, and the fetch effect picks it up.
    setStructuredFilters(next);
    sentenceFilterRef.current?.clearQuery();
  };

  const handleQuickSearchChange = (value: string) => {
    setQuickSearch(value);
    if (quickSearchDebounceRef.current) clearTimeout(quickSearchDebounceRef.current);
    quickSearchDebounceRef.current = setTimeout(() => applyQuickSearch(value), 300);
  };

  const handleQuickSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyQuickSearch(quickSearch);
    } else if (event.key === "Escape") {
      setQuickSearch("");
      applyQuickSearch("");
    }
  };

  useEffect(() => {
    return () => {
      if (quickSearchDebounceRef.current) clearTimeout(quickSearchDebounceRef.current);
    };
  }, []);

  const handleSortChange = (value: string) => {
    setCurrentSortBy(value);
    // Parse the field and direction from the value (format: "field:direction")
    const [field, direction] = value.split(":");
    const isDesc = direction === "desc";
    setSortingState([{ id: field, desc: isDesc }]);
  };

  // Handle applying sentence-based search filters
  const handleSentenceFiltersApplied = (
    filters: PatientFilterResult,
    results: PatientQueryResults,
    query: string
  ) => {
    // Also clears structured filters
    setSmartSearch(query, filters);
    setQuickSearch("");
  };
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isScheduleCallDialogOpen, setIsScheduleCallDialogOpen] =
    useState(false);
  const [isScheduleCheckinCallDialogOpen, setIsScheduleCheckinCallDialogOpen] =
    useState(false);
  const [isScheduleBatchCallDialogOpen, setIsScheduleBatchCallDialogOpen] =
    useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isHtmlUploadModalOpen, setIsHtmlUploadModalOpen] = useState(false);
  const [isGoogleDriveModalOpen, setIsGoogleDriveModalOpen] = useState(false);
  const { fetchConsentsForCurrentOrg } = useConsentsStore();
  const { selectedOrganizationId } = useOrganizationsStore();
  const { defaultPhoneNumberId, fetchVerifiedPhoneNumbers } =
    useVerifiedPhoneNumbersStore();

  // Handle applying structured filters
  const handleApplyStructuredFilters = (newFilters: PatientFilterResult) => {
    // Also clears sentence search
    setStructuredFilters(newFilters);
    if (!shallowEqualFilters(newFilters, quickSearchToFilters(quickSearch))) {
      setQuickSearch("");
    }
    // The useEffect will handle fetching with the new filters
  };

  // Handle clearing structured filters
  const handleClearStructuredFilters = () => {
    clearSmartSearch();
    sentenceFilterRef.current?.clearQuery();
    setQuickSearch("");
    // The useEffect will handle fetching all patients when filters become null
  };

  // Handle clearing sentence search
  const handleClearSentenceSearch = () => {
    clearSmartSearch();
    sentenceFilterRef.current?.clearQuery();
    setQuickSearch("");
    // The useEffect will handle fetching all patients when filters become null
  };

  // Handle clearing all filters (both sentence and structured)
  const handleClearAllFilters = () => {
    clearSmartSearch();
    sentenceFilterRef.current?.clearQuery();
    setQuickSearch("");
  };

  // Update store when sorting changes or pagination changes
  useEffect(() => {
    if (selectedOrganizationId) {
      // Prioritize sentence search over structured filters
      if (sentenceSearchFilters && Object.keys(sentenceSearchFilters).length > 0) {
        // Use v3 endpoint with sentence search filters
        fetchPatientsV3(selectedOrganizationId, sentenceSearchFilters);
      } else if (activeStructuredFilters && Object.keys(activeStructuredFilters).length > 0) {
        // Use v3 endpoint with structured filters
        fetchPatientsV3(selectedOrganizationId, activeStructuredFilters);
      } else {
        // Use the advanced endpoint with server-side pagination (all patients)
        fetchPatientsAdvanced(selectedOrganizationId);
      }

      // Always fetch consents as they might change more frequently
      fetchConsentsForCurrentOrg();

      // Fetch verified phone numbers
      fetchVerifiedPhoneNumbers(selectedOrganizationId);
    }
  }, [
    selectedOrganizationId,
    fetchPatientsAdvanced,
    fetchPatientsV3,
    fetchConsentsForCurrentOrg,
    fetchVerifiedPhoneNumbers,
    currentPage,
    pageSize,
    sortingState,
    filters, // Re-fetch when regular filters change
    sentenceSearchFilters, // Re-fetch when sentence search filters change
    activeStructuredFilters, // Re-fetch when structured filters change
  ]);

  // Initialize sort state from store on component mount
  useEffect(() => {
    if (sortingState.length > 0) {
      const sort = sortingState[0];
      const direction = sort.desc ? "desc" : "asc";
      setCurrentSortBy(`${sort.id}:${direction}`);
    }
  }, []);

  const downloadSkippedIds = (ids: string[]) => {
    const content = ids.join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "skipped_patient_ids.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !selectedOrganizationId) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.ingestPatientFilesFromCSV(
        selectedOrganizationId,
        formData
      );
      const baseMessage = `${response.created} patients created.\nTotal processed: ${response.total_processed}`;
      const skippedMessage =
        response.skipped.length > 0
          ? `\n\nSkipped ${
              response.skipped.length
            } existing patients:\n${response.skipped.join("\n")}`
          : "";

      toast({
        title: "Success",
        description: (
          <div className="whitespace-pre-wrap">
            {baseMessage + skippedMessage}
            {response.skipped.length > 0 && (
              <div className="mt-2">
                <button
                  className="px-3 py-1 bg-muted hover:bg-border rounded text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadSkippedIds(response.skipped);
                  }}
                >
                  Download Skipped IDs
                </button>
              </div>
            )}
          </div>
        ),
        variant: "default",
      });
      fetchPatientsAdvanced(selectedOrganizationId);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload patient data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset the file input
      event.target.value = "";
    }
  };

  const handleCarePlanImport = () => {
    // Create a file input element
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.doc,.docx,.txt";
    input.style.display = "none";

    input.onchange = async (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files || files.length === 0 || !selectedOrganizationId) return;

      setIsUploading(true);

      try {
        const fileArray = Array.from(files);
        await api.ingestCarePlan(selectedOrganizationId, fileArray);

        toast({
          title: "Success",
          description: `Successfully imported ${fileArray.length} care plan file(s)`,
          variant: "default",
        });

        // Refresh the patient list to show updated care plans
        fetchPatientsAdvanced(selectedOrganizationId);
      } catch (error) {
        console.error("Error importing care plans:", error);
        toast({
          title: "Error",
          description: "Failed to import care plans. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
        // Clean up the input element
        document.body.removeChild(input);
      }
    };

    // Add to body and click
    document.body.appendChild(input);
    input.click();
  };

  const handleGenerateCarePlanPDFs = async () => {
    if (!selectedOrganizationId) return;

    setIsUploading(true);

    try {
      const result = await api.generateMissingCarePlanPDFs(
        selectedOrganizationId,
        150
      );

      toast({
        title: "Success",
        description:
          result || "Care plan PDF generation initiated successfully",
        variant: "default",
      });
    } catch (error) {
      console.error("Error generating care plan PDFs:", error);
      toast({
        title: "Error",
        description: "Failed to generate care plan PDFs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const hasStructuredFilters = activeStructuredFilters && Object.keys(activeStructuredFilters).length > 0;
    return (
      hasStructuredFilters ||
      !!filters.search ||
      !!filters.gender ||
      (!!filters.tags && filters.tags.length > 0) ||
      (!!filters.apcm_consent_status &&
        filters.apcm_consent_status.length > 0) ||
      (!!filters.latest_call_status && filters.latest_call_status.length > 0)
    );
  }, [filters, activeStructuredFilters]);

  // Function to handle page size change
  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value));
    setCurrentPage(0); // Reset to first page when changing page size
  };

  if (error) {
    return (
      <div className="container mx-auto py-10">
        <div className="bg-destructive/15 p-4 rounded-md">
          <p className="text-destructive">Error loading patients: {error}</p>
          <div className="flex gap-2 mt-2">
            <button
              className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded"
              onClick={() =>
                fetchPatientsAdvanced(selectedOrganizationId as string)
              }
            >
              Retry
            </button>
            <button
              className="px-4 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded"
              onClick={() => {
                // Clear localStorage for patients store
                localStorage.removeItem('patients-storage');
                // Refresh the page
                window.location.reload();
              }}
            >
              Reset & Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full w-full flex-col items-stretch px-4 pt-4 md:px-8 md:pt-6 ${
        selectedPatients.length > 0 ? "pb-28 md:pb-0" : ""
      }`}
    >
      <PatientActionBar
        onNewPatient={() => navigate("/patients/new")}
        onFileUpload={handleFileUpload}
        onHtmlUpload={() => setIsHtmlUploadModalOpen(true)}
        onGoogleDriveImport={() => setIsGoogleDriveModalOpen(true)}
        onCarePlanImport={handleCarePlanImport}
        onGenerateCarePlanPDFs={handleGenerateCarePlanPDFs}
        isUploading={isUploading}
        defaultPhoneNumberId={defaultPhoneNumberId}
        hasActiveFilters={hasActiveFilters}
        filterCount={
          (activeStructuredFilters ? Object.keys(activeStructuredFilters).length : 0) +
          Object.values(filters).filter(
            (value) =>
              value !== null && (Array.isArray(value) ? value.length > 0 : true)
          ).length
        }
      />

      {/* Unified Filter Bar */}
      <div className="space-y-3 mb-4">
        {/* Filter Inputs Row */}
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="w-full flex-1 min-w-0">
            <CompactSentenceFilter
              ref={sentenceFilterRef}
              config={patientFilterConfig}
              onFiltersApplied={handleSentenceFiltersApplied}
            />
          </div>
          {/* Plain quick search: name, phone, MRN or FHIR id (no LLM) */}
          <div className="relative w-full md:w-72 md:flex-none">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={quickSearch}
              onChange={(e) => handleQuickSearchChange(e.target.value)}
              onKeyDown={handleQuickSearchKeyDown}
              placeholder="Search name, phone, MRN or FHIR ID"
              aria-label="Search patients by name, phone, MRN or FHIR ID"
              className="pl-8"
            />
          </div>
          <PatientFilterBuilder
            onApplyFilters={handleApplyStructuredFilters}
            activeFilters={activeStructuredFilters}
            onClearFilters={handleClearStructuredFilters}
          />
          {/* Clear All button - visible when any filters are active */}
          {(sentenceSearchFilters || activeStructuredFilters) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAllFilters}
              className="text-muted-foreground hover:text-destructive whitespace-nowrap"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        {/* Active Filters Summary */}
        {((sentenceSearchFilters && Object.keys(sentenceSearchFilters).length > 0) ||
          (activeStructuredFilters && Object.keys(activeStructuredFilters).length > 0) ||
          sentenceSearchQuery) && (
          <Card className="bg-primary border-primary">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-2 flex-wrap">
                {sentenceSearchQuery && (
                  <>
                    <span className="text-sm font-medium text-primary-foreground">
                      Sentence Query:
                    </span>
                    <Badge variant="secondary" className="text-xs bg-primary-foreground/15 text-primary-foreground">
                      {sentenceSearchQuery}
                    </Badge>
                  </>
                )}

                {activeStructuredFilters && Object.keys(activeStructuredFilters).length > 0 && (
                  <>
                    {sentenceSearchQuery && (
                      <span className="text-primary-foreground/70">•</span>
                    )}
                    <span className="text-sm font-medium text-primary-foreground">
                      Structured Filters:
                    </span>
                    {Object.entries(activeStructuredFilters).map(([key, value]) => {
                      if (value === null || value === undefined || value === '') return null;

                      let displayValue = String(value);
                      if (typeof value === 'boolean') {
                        displayValue = value ? 'Yes' : 'No';
                      } else if (Array.isArray(value)) {
                        displayValue = value.join(', ');
                      }

                      return (
                        <Badge key={key} variant="secondary" className="text-xs bg-primary-foreground/15 text-primary-foreground">
                          {key}: {displayValue}
                        </Badge>
                      );
                    })}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {isLoading ? (
        // Loading skeleton
        <div className="space-y-4 flex-1">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex space-x-4 overflow-hidden">
              <Skeleton className="h-10 w-[200px]" />
              <Skeleton className="h-10 w-[100px]" />
              <Skeleton className="h-10 w-[150px]" />
              <Skeleton className="h-10 w-[100px]" />
              <Skeleton className="h-10 w-[100px]" />
            </div>
          ))}
        </div>
      ) : (
        <Card className="pt-4 md:pt-6 flex-1 flex flex-col min-h-0">
          <CardContent className="flex flex-col md:flex-row items-stretch gap-4 flex-1 min-h-0 px-3 md:px-6">
            {/* Table and Pagination Column */}
            <div className="flex-1 flex flex-col min-h-0">
              <PatientTable
                patients={patients}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
              />
              <PatientPagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                rowSelectionCount={selectedPatients.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageSizeChange={handlePageSizeChange}
                canPreviousPage={currentPage > 0}
                canNextPage={currentPage < totalPages - 1}
                sortBy={currentSortBy}
                onSortChange={handleSortChange}
              />
            </div>

            {/* Patient Selection Sidebar */}
            {selectedPatients.length > 0 && (
              <div className="fixed inset-x-0 bottom-0 z-30 md:static md:z-auto md:w-80 md:flex-shrink-0">
                <PatientSelectionSidebar
                  selectedPatients={selectedPatients}
                  onRemovePatient={removePatientSelection}
                  onCallPatients={() => setIsCallDialogOpen(true)}
                  onScheduleCall={() => setIsScheduleCallDialogOpen(true)}
                  onScheduleCheckinCall={() =>
                    setIsScheduleCheckinCallDialogOpen(true)
                  }
                  onScheduleBatchCall={() =>
                    setIsScheduleBatchCallDialogOpen(true)
                  }
                  defaultPhoneNumberId={defaultPhoneNumberId}
                  onClearAllPatients={clearSelections}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* APCM Call Dialog for multiple patients */}
      {selectedPatients.length > 0 && (
        <>
          <APCMCallDialog
            open={isCallDialogOpen}
            onOpenChange={setIsCallDialogOpen}
            patients={selectedPatients}
            verifiedPhoneNumberId={defaultPhoneNumberId || ""}
            onSuccess={() => {
              // Clear selections after successful call
              clearSelections();
            }}
          />
          <ScheduleAPCMCallsDialog
            open={isScheduleCallDialogOpen}
            onOpenChange={setIsScheduleCallDialogOpen}
            patients={selectedPatients}
            verifiedPhoneNumberId={defaultPhoneNumberId || ""}
            onSuccess={() => {
              // Clear selections after successful scheduling
              clearSelections();
            }}
          />
          <ScheduleAPCMCheckinCallsDialog
            open={isScheduleCheckinCallDialogOpen}
            onOpenChange={setIsScheduleCheckinCallDialogOpen}
            patients={selectedPatients}
            verifiedPhoneNumberId={defaultPhoneNumberId || ""}
            onSuccess={() => {
              // Clear selections after successful scheduling
              clearSelections();
            }}
          />

          <ScheduleBatchCallsDialog
            open={isScheduleBatchCallDialogOpen}
            onOpenChange={setIsScheduleBatchCallDialogOpen}
            patients={selectedPatients}
            verifiedPhoneNumberId={defaultPhoneNumberId || ""}
            onSuccess={() => {
              // Clear selections after successful scheduling
              clearSelections();
            }}
          />
        </>
      )}

      {/* HTML File Upload Modal */}
      <PatientFileUploadModal
        isOpen={isHtmlUploadModalOpen}
        onClose={() => setIsHtmlUploadModalOpen(false)}
        organizationId={selectedOrganizationId || ""}
        onUploadComplete={() => {
          fetchPatientsAdvanced(selectedOrganizationId as string);
        }}
      />

      {/* Google Drive Import Modal */}
      <PatientGoogleDriveImportModal
        isOpen={isGoogleDriveModalOpen}
        onClose={() => setIsGoogleDriveModalOpen(false)}
        organizationId={selectedOrganizationId || ""}
        onImportComplete={() => {
          fetchPatientsAdvanced(selectedOrganizationId as string);
        }}
      />
    </div>
  );
};

export default PatientsPage;
